import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Calendar, ArrowRight, Check, Route, Clock, 
  RefreshCw, Map, Zap, Truck, Settings, Sparkles, MessageSquare
} from 'lucide-react';

const SchedulingPage = () => {
  const features = [
    {
      icon: RefreshCw,
      title: "Recurring Schedules",
      description: "Set up weekly, biweekly, monthly, seasonal, or custom recurring schedules. Jobs are automatically generated—no manual entry required."
    },
    {
      icon: Route,
      title: "Route Optimization",
      description: "Group jobs by location to minimize drive time. Our system suggests optimal routes based on property proximity."
    },
    {
      icon: Calendar,
      title: "Calendar View",
      description: "See your entire schedule at a glance. View by day, week, or month. Drag and drop to reschedule jobs."
    },
    {
      icon: Clock,
      title: "Time Estimates",
      description: "Track estimated vs actual job times to improve scheduling accuracy and crew efficiency over time."
    },
    {
      icon: Map,
      title: "Visual Route Planning",
      description: "See jobs on a map to understand geographic distribution and build logical routes that make sense."
    },
    {
      icon: Settings,
      title: "Flexible Scheduling",
      description: "Handle one-time jobs, special requests, and schedule changes without disrupting your regular routes."
    }
  ];

  const schedulingTypes = [
    { label: "Weekly", description: "Every week on the same day", popular: true },
    { label: "Biweekly", description: "Every other week", popular: true },
    { label: "Monthly", description: "Once per month", popular: false },
    { label: "Seasonal", description: "Spring/Summer/Fall schedules", popular: false },
    { label: "Custom", description: "Any interval you need", popular: false }
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Calendar className="h-4 w-4" />
                Core Feature
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                Scheduling & Route Optimization
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Automate recurring schedules, optimize routes for efficiency, and give your crews 
                clear daily plans. Save hours every week on scheduling alone.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg" className="bg-primary hover:bg-primary/90">
                    Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1690068023694-053da714f95f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxsYXduJTIwbW93ZXIlMjBncmVlbiUyMGdyYXNzJTIwY3V0dGluZ3xlbnwwfHx8fDE3NzM3OTQ2OTR8MA&ixlib=rb-4.1.0&q=85"
                alt="Lawn mowing service"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Scheduling Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Flexible Recurring Schedules</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Set it and forget it. Create service plans that automatically generate jobs at whatever frequency your customers need.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
            {schedulingTypes.map((type, index) => (
              <div key={index} className={`p-4 rounded-xl border-2 text-center ${type.popular ? 'border-primary bg-primary/5' : 'border-gray-200'}`}>
                <p className="font-semibold text-gray-900">{type.label}</p>
                <p className="text-xs text-gray-500 mt-1">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Powerful Scheduling Tools</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to keep your crews busy with efficient, well-planned routes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-gray-100 bg-white hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="bg-blue-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Route Optimization Impact</h3>
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-4 flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Zap className="h-8 w-8 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">30%</p>
                    <p className="text-gray-500">Less drive time</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-center gap-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Truck className="h-8 w-8 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">20%</p>
                    <p className="text-gray-500">More jobs per day</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-center gap-4">
                  <div className="w-16 h-16 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="h-8 w-8 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">10 hrs</p>
                    <p className="text-gray-500">Saved weekly on scheduling</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Stop Wasting Time on Manual Scheduling
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Most landscaping companies spend hours every week figuring out who goes where. 
                With Orchard LMS, you set up the schedule once and let the system handle the rest.
              </p>
              <ul className="space-y-4">
                {[
                  "Automatic job generation from recurring plans",
                  "Smart route suggestions based on geography",
                  "Easy drag-and-drop rescheduling",
                  "Real-time updates when things change",
                  "Never double-book a crew again"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AI Integration Section */}
      <section className="py-20 bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                AI-Powered
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Schedule Jobs by Simply Asking
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our AI Assistant understands natural language scheduling requests. Tell it when you want a 
                job done, and it handles the rest—including crew assignment and route optimization.
              </p>
              <ul className="space-y-4">
                {[
                  "Schedule jobs with natural language",
                  "AI finds the best time slot automatically",
                  "Check today's schedule instantly",
                  "Reschedule with a simple request"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/features/ai-assistant">
                  <Button variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50">
                    Learn About AI Assistant <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-violet-100 overflow-hidden">
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-semibold text-white">AI Assistant</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-violet-50 rounded-lg p-4 ml-8">
                  <p className="text-sm text-gray-700">"Schedule the Smith property for next Tuesday morning"</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mr-8">
                  <p className="text-sm text-gray-700">I'll schedule the Smith property job for Tuesday:</p>
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                    <p className="text-sm font-medium">789 Pine Road - Weekly Mowing</p>
                    <p className="text-sm text-gray-500">Tuesday, Jan 14 at 9:00 AM</p>
                    <p className="text-sm text-blue-600">Duration: ~45 minutes</p>
                  </div>
                  <p className="text-sm text-gray-700 mt-3">Want me to schedule this job?</p>
                </div>
                <div className="flex gap-2 justify-end">
                  <span className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg">Yes, schedule it</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Start Optimizing Your Routes Today
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Join landscaping companies saving hours every week with smart scheduling and route optimization.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default SchedulingPage;
